// Batch I — Scenario comparison (asking vs counter-offer).
//
// Side-by-side 6-metric grid on Deal Detail. Both columns are fed by
// POST /api/intelligence/deal-at-price (no new backend needed). The
// counter column is editable inline → PATCH /deals/{id} with the
// canonical Phase 15 / Batch G `counter_offer_price` field.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Check, Loader2, Pencil, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/Skeleton";
import VerdictBadge from "@/components/VerdictBadge";
import { useFmt } from "@/lib/useFmt";

function effectiveCounter(deal) {
  return (
    deal.counter_offer_price ??
    deal?.ai_negotiation?.recommended_counter_offer ??
    deal.purchase_price ??
    0
  );
}

function Delta({ before, after, unit }) {
  const a = Number(before || 0);
  const b = Number(after || 0);
  const diff = b - a;
  if (Math.abs(diff) < 0.005) return null;
  const positive = diff > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
        positive ? "text-emerald-700" : "text-rose-700"
      }`}
    >
      {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {positive ? "+" : ""}
      {Math.abs(diff).toFixed(2)}
      {unit}
    </span>
  );
}

function MoneyDelta({ before, after }) {
  const fmt = useFmt();
  const a = Number(before || 0);
  const b = Number(after || 0);
  const diff = b - a;
  if (Math.abs(diff) < 0.5) return null;
  const positive = diff > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
        positive ? "text-emerald-700" : "text-rose-700"
      }`}
    >
      {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {positive ? "+" : ""}
      {fmt.money(diff, { primaryOnly: true, cents: true })}
    </span>
  );
}

// Stack one labelled metric row used inside both columns.
function MetricRow({ label, value, delta = null }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 py-2.5 last:border-b-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="flex items-baseline gap-1.5 tabular-nums text-sm font-semibold text-slate-900">
        {value}
        {delta}
      </span>
    </div>
  );
}

export default function ScenarioComparison({ deal, onCounterSaved }) {
  const fmt = useFmt();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(effectiveCounter(deal) || ""));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ask, setAsk] = useState(null);
  const [counter, setCounter] = useState(null);

  const counterPrice = effectiveCounter(deal);
  // Phase 48.1 — "asking" is the seller list, NOT the effective/counter
  // price stored in deal.purchase_price. Prefer the backend-stamped
  // asking_price_display, fall back through the canonical chain.
  const askingPrice = Number(
    deal?.asking_price_display ??
      deal?.original_purchase_price ??
      deal?.property?.asking_price ??
      deal?.purchase_price ??
      0
  );
  const savedFromAi =
    deal.counter_offer_price == null && (deal?.ai_negotiation?.recommended_counter_offer ?? 0) > 0;
  const noCounterSet = !(deal.counter_offer_price > 0);

  // Run both deal-at-price calls in parallel on mount / when the
  // canonical counter changes.
  const refresh = useCallback(async () => {
    if (askingPrice <= 0) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.post("/intelligence/deal-at-price", {
          deal_id: deal.id,
          price: askingPrice,
        }),
        api.post("/intelligence/deal-at-price", {
          deal_id: deal.id,
          price: counterPrice,
        }),
      ]);
      setAsk(a.data);
      setCounter(b.data);
    } catch (err) {
      console.warn("scenario comparison refresh failed", err);
    } finally {
      setLoading(false);
    }
  }, [deal.id, askingPrice, counterPrice]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep the editor's draft in sync when the canonical counter changes.
  useEffect(() => {
    setDraft(String(counterPrice || ""));
  }, [counterPrice]);

  const applyCounter = async (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Enter a valid counter price.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/deals/${deal.id}`, {
        analysis: { counter_offer_price: num },
      });
      toast.success("Counter saved");
      onCounterSaved?.(num);
      setEditing(false);
    } catch (err) {
      console.error("counter save failed", err);
      toast.error("Could not save counter.");
    } finally {
      setSaving(false);
    }
  };

  const useAiSuggestion = () => {
    const aiSuggested = deal?.ai_negotiation?.recommended_counter_offer;
    if (aiSuggested && aiSuggested > 0) {
      applyCounter(aiSuggested);
    } else {
      toast.error("No AI recommendation available — run AI analysis first.");
    }
  };

  const savingsDollar = askingPrice - counterPrice;
  const savingsPercent = askingPrice > 0 ? (savingsDollar / askingPrice) * 100 : 0;

  const verdictAtAsking = ask?.verdict || "Hold";
  const verdictAtCounter = counter?.verdict || "Hold";
  const gradeImproved = counter?.verdict_improved_grades ?? 0;

  const m = useMemo(() => ask?.metrics || {}, [ask]);
  const n = useMemo(() => counter?.metrics || {}, [counter]);

  if (askingPrice <= 0) return null;

  return (
    <section
      data-testid="scenario-comparison"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <header className="mb-5 flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">
            Scenario comparison
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900">
            Asking vs your counter-offer
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Side-by-side numbers at both prices. Re-underwritten live —
            edit the counter to see how the verdict shifts.
          </p>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* AT ASKING column */}
        <div
          data-testid="scenario-at-asking"
          className="rounded-xl border border-slate-200 bg-slate-50/30 p-5"
        >
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              At asking
            </p>
            <p className="text-lg font-semibold tabular-nums text-slate-900">
              {fmt.money(askingPrice, { primaryOnly: true })}
            </p>
          </div>
          {loading ? (
            <Skeleton className="h-48 rounded-md" />
          ) : (
            <>
              <MetricRow label="Monthly cash flow" value={fmt.money(m.monthly_cash_flow, { primaryOnly: true, cents: true })} />
              <MetricRow label="Annual cash flow"  value={fmt.money(m.annual_cash_flow,  { primaryOnly: true })} />
              <MetricRow label="Cap rate"          value={`${(m.cap_rate ?? 0).toFixed(2)}%`} />
              <MetricRow label="Cash-on-cash"      value={`${(m.coc_return ?? 0).toFixed(2)}%`} />
              <MetricRow label="DSCR"              value={(m.dscr ?? 0) > 0 ? `${(m.dscr).toFixed(2)}x` : "N/A"} />
              <MetricRow label="Total cash needed" value={fmt.money(m.total_cash_needed, { primaryOnly: true })} />
              <MetricRow label="Verdict"           value={<VerdictBadge verdict={verdictAtAsking} size="sm" />} />
            </>
          )}
        </div>

        {/* AT COUNTER column */}
        <div
          data-testid="scenario-at-counter"
          className={`rounded-xl border p-5 ${
            noCounterSet
              ? "border-dashed border-slate-300 bg-slate-50/40"
              : "border-indigo-200 bg-indigo-50/30"
          }`}
        >
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <div className="flex items-baseline gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                At your counter
              </p>
              {savedFromAi ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                  title="Using AI's recommended counter — set your own below."
                >
                  <Sparkles className="h-2.5 w-2.5" /> AI
                </span>
              ) : null}
            </div>
            {editing ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">$</span>
                <input
                  type="number"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  data-testid="scenario-counter-input"
                  autoFocus
                  className="w-28 rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-sm tabular-nums shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={() => applyCounter(draft)}
                  disabled={saving}
                  data-testid="scenario-counter-apply"
                  className="rounded-md bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setDraft(String(counterPrice || "")); }}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid="scenario-counter-edit"
                className="group inline-flex items-baseline gap-1.5 rounded-md px-1 text-lg font-semibold tabular-nums text-slate-900 hover:bg-slate-100"
              >
                {fmt.money(counterPrice, { primaryOnly: true })}
                <Pencil className="h-3 w-3 text-slate-400 opacity-0 transition group-hover:opacity-100" />
              </button>
            )}
          </div>

          {!editing && counterPrice > 0 && savingsDollar > 0 ? (
            <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
              <ArrowDown className="h-3 w-3" />
              Save {fmt.money(savingsDollar, { primaryOnly: true })} ({savingsPercent.toFixed(1)}%)
            </div>
          ) : null}

          {loading ? (
            <Skeleton className="h-48 rounded-md" />
          ) : (
            <>
              <MetricRow
                label="Monthly cash flow"
                value={fmt.money(n.monthly_cash_flow, { primaryOnly: true, cents: true })}
                delta={<MoneyDelta before={m.monthly_cash_flow} after={n.monthly_cash_flow} />}
              />
              <MetricRow
                label="Annual cash flow"
                value={fmt.money(n.annual_cash_flow, { primaryOnly: true })}
              />
              <MetricRow
                label="Cap rate"
                value={`${(n.cap_rate ?? 0).toFixed(2)}%`}
                delta={<Delta before={m.cap_rate} after={n.cap_rate} unit="%" />}
              />
              <MetricRow
                label="Cash-on-cash"
                value={`${(n.coc_return ?? 0).toFixed(2)}%`}
                delta={<Delta before={m.coc_return} after={n.coc_return} unit="%" />}
              />
              <MetricRow
                label="DSCR"
                value={(n.dscr ?? 0) > 0 ? `${(n.dscr).toFixed(2)}x` : "N/A"}
                delta={(n.dscr ?? 0) > 0 && (m.dscr ?? 0) > 0
                  ? <Delta before={m.dscr} after={n.dscr} unit="x" />
                  : null}
              />
              <MetricRow
                label="Total cash needed"
                value={fmt.money(n.total_cash_needed, { primaryOnly: true })}
                delta={<MoneyDelta before={m.total_cash_needed} after={n.total_cash_needed} />}
              />
              <MetricRow
                label="Verdict"
                value={
                  <span className="inline-flex items-center gap-2">
                    <VerdictBadge verdict={verdictAtCounter} size="sm" />
                    {gradeImproved > 0 ? (
                      <span
                        data-testid="scenario-verdict-shift"
                        className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
                      >
                        <ArrowUp className="h-2.5 w-2.5" /> +{gradeImproved}
                      </span>
                    ) : gradeImproved < 0 ? (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200">
                        <ArrowDown className="h-2.5 w-2.5" /> {gradeImproved}
                      </span>
                    ) : null}
                  </span>
                }
              />
            </>
          )}

          {noCounterSet ? (
            <div className="mt-4 flex flex-col items-start gap-2 border-t border-slate-200 pt-4 text-sm">
              <p className="text-slate-600">
                No counter set yet — using AI's recommended counter as a placeholder.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                data-testid="scenario-set-counter"
              >
                Set a counter to compare
              </Button>
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-500">
              <button
                type="button"
                onClick={useAiSuggestion}
                data-testid="scenario-use-ai"
                className="text-indigo-600 hover:underline"
              >
                Use AI suggestion
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
